/**
 * Error Parser Utility
 * 
 * Centralized error parsing for API responses, specifically for email verification errors.
 * Handles rate limiting, error codes, and retry logic.
 */
import { EmailVerificationErrorCode } from '@hummii/shared-types';
import { ApiError } from '@shared/api';

/**
 * Parsed verification error result
 */
export interface ParsedVerificationError {
  type: 'rate_limit' | 'generic' | 'network';
  message: string;
  retryAfter?: number; // Seconds to wait before retry
  errorCode?: EmailVerificationErrorCode;
}

/**
 * Parse email verification error from API response
 * 
 * @param error - ApiError from fetch/axios
 * @returns Parsed error with type, message, and retry info
 */
export function parseVerificationError(error: unknown): ParsedVerificationError {
  // Handle ApiError instance
  if (error && typeof error === 'object' && 'status' in error) {
    const apiError = error as ApiError;
    
    // Rate limiting (429 Too Many Requests)
    if (apiError.status === 429) {
      const retryAfter = extractRetryAfter(apiError);
      
      return {
        type: 'rate_limit',
        message: apiError.message || 'Too many requests',
        retryAfter,
        errorCode: EmailVerificationErrorCode.RESEND_LIMIT_REACHED,
      };
    }
    
    // Email not verified (403 Forbidden)
    if (
      apiError.status === 403 &&
      apiError.response &&
      typeof apiError.response.code === 'string' &&
      apiError.response.code === 'EMAIL_NOT_VERIFIED'
    ) {
      return {
        type: 'generic',
        message: apiError.message || 'Email not verified',
        errorCode: EmailVerificationErrorCode.EMAIL_NOT_VERIFIED,
      };
    }
    
    // Other API errors
    return {
      type: 'generic',
      message: apiError.message || 'An error occurred',
    };
  }
  
  // Network or unknown errors
  if (error instanceof Error) {
    return {
      type: 'network',
      message: error.message || 'Network error',
    };
  }
  
  // Fallback for unknown error types
  return {
    type: 'generic',
    message: 'An unexpected error occurred',
  };
}

/**
 * Extract retry-after value from error response
 * 
 * Checks multiple sources:
 * 1. retryAfter field in response body
 * 2. Retry-After header (if available)
 * 3. Default to 180 seconds (3 minutes) for rate limiting
 * 
 * @param error - ApiError instance
 * @returns Retry-after time in seconds
 */
function extractRetryAfter(error: ApiError): number {
  // Check response body for retryAfter field
  if (error.response && typeof error.response === 'object') {
    const response = error.response as Record<string, unknown>;
    
    if (typeof response.retryAfter === 'number') {
      return response.retryAfter;
    }
    
    // Some APIs return it as string
    if (typeof response.retryAfter === 'string') {
      const parsed = parseInt(response.retryAfter, 10);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
  }
  
  // Default: 3 minutes for rate limiting
  return 180;
}

/**
 * Format retry time as human-readable string
 * 
 * @param seconds - Time in seconds
 * @returns Formatted string (e.g., "3 minutes", "1 minute", "45 seconds")
 */
export function formatRetryTime(seconds: number): string {
  if (seconds >= 60) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  
  return `${seconds} second${seconds > 1 ? 's' : ''}`;
}

/**
 * Calculate minutes from seconds (for translation placeholders)
 * 
 * @param seconds - Time in seconds
 * @returns Minutes rounded up
 */
export function secondsToMinutes(seconds: number): number {
  return Math.ceil(seconds / 60);
}
